import { useState } from "react";
import { Modal } from "react-bootstrap";
import { EngagementService } from "@services/engagements/engagementService";
import { KeyDateService } from "@services/engagements/keyDateService";
import type { KeyDate, KeyDateType } from "@models/keyDate.model";

/*
    Two scopes, because key dates are mid-move (MPN-2185). A campaign passes `launchId`; the
    objective screen still passes its engagement and objective. One modal rather than two:
    the form is identical and only the save target differs.
*/
type Props = {
  launchId?: number;
  engagementId?: number;
  objectiveId?: number;
  /** null when adding; the existing date when editing. */
  keyDate: KeyDate | null;
  keyDateTypes: KeyDateType[];
  onClose: () => void;
  onSaved: () => void;
};

/*
    One key date, added or edited. The type catalogue decides whether a label is
    needed: a custom date has no meaning without one, a catalogued date already
    carries its own name.
*/
export default function KeyDateModal({
  launchId,
  engagementId,
  objectiveId,
  keyDate,
  keyDateTypes,
  onClose,
  onSaved,
}: Props) {
  const isEditing = keyDate?.id != null;

  /*
      Saving from a campaign copies an inherited date rather than editing the shared row, so
      the siblings keep theirs. Worth saying plainly: the row on screen came from the
      objective, and the edit will not reach the campaigns that share it. Editing from the
      objective screen does change the shared row, so it says nothing.
  */
  const keepTheirs =
    launchId != null && isEditing && keyDate?.is_inherited
      ? (keyDate.shared_with ?? [])
          .filter((campaign) => campaign.id !== launchId)
          .map((campaign) => campaign.name)
      : [];
  const [typeId, setTypeId] = useState<number | null>(
    keyDate?.key_date_type_id ?? keyDateTypes[0]?.id ?? null,
  );
  const [label, setLabel] = useState(keyDate?.label ?? "");
  const [date, setDate] = useState(toInputDate(keyDate?.date));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isCustom = keyDateTypes.find((type) => type.id === typeId)?.is_custom ?? false;
  const canSubmit = typeId != null && date !== "" && (!isCustom || label.trim() !== "") && !isSaving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setIsSaving(true);
    setError(null);
    const payload = {
      key_date_type_id: typeId,
      label: isCustom ? label.trim() : null,
      date,
    };
    try {
      if (launchId != null) {
        const service = new KeyDateService();
        if (isEditing) {
          await service.updateForCampaign(launchId, keyDate!.id!, payload as Partial<KeyDate>);
        } else {
          await service.createForCampaign(launchId, payload as Partial<KeyDate>);
        }
      } else {
        const service = new EngagementService();
        if (isEditing) {
          await service.updateKeyDates(engagementId!, String(objectiveId), keyDate!.id!, payload);
        } else {
          await service.createKeyDates(engagementId!, objectiveId!, payload as KeyDate);
        }
      }
      onSaved();
    } catch {
      setError("Could not save that date. Try again.");
      setIsSaving(false);
    }
  };

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1rem" }}>{isEditing ? "Edit key date" : "New key date"}</Modal.Title>
      </Modal.Header>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <Modal.Body>
          {error && <div className="alert alert-danger py-2 small">{error}</div>}

          {keepTheirs.length > 0 && (
            <div className="small text-muted mb-3">
              This date comes from the objective. Saving gives this campaign its own copy —{" "}
              {keepTheirs.join(", ")} keep theirs.
            </div>
          )}

          <div className="mb-3">
            <label htmlFor="kdType" className="form-label small fw-semibold">
              What kind of date
            </label>
            <select
              id="kdType"
              className="form-select"
              value={typeId ?? ""}
              onChange={(e) => setTypeId(e.target.value ? Number(e.target.value) : null)}
            >
              {keyDateTypes.length === 0 && <option value="">No date types available</option>}
              {keyDateTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {isCustom && (
            <div className="mb-3">
              <label htmlFor="kdLabel" className="form-label small fw-semibold">
                Name it
              </label>
              <input
                id="kdLabel"
                className="form-control"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Priority deadline"
                autoFocus
              />
            </div>
          )}

          <div>
            <label htmlFor="kdDate" className="form-label small fw-semibold">
              Date
            </label>
            <input
              id="kdDate"
              className="form-control"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-sm btn-primary" disabled={!canSubmit}>
            {isSaving ? "Saving..." : isEditing ? "Save date" : "Add date"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

/** The API returns a datetime; a date input only accepts YYYY-MM-DD. */
function toInputDate(value: string | null | undefined): string {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
